import React, { useEffect, useRef, useState } from "react";
import PropTypes from 'prop-types';
import _uniqueId from 'lodash/uniqueId';
import { Icon, Typography } from "../../index";
import './text_field.scss';

export const TextField = React.forwardRef(({
    autoFocus = false,
    className,
    colorStyle = 'neutral',
    disabled = false,
    error = false,
    hintMsg,
    icon,
    id,
    label,
    maxLength,
    name,
    onBlur = () => { },
    onChange = () => { },
    onFocus = () => { },
    postIcon,
    required = false,
    value,
    ...inputProps
}, ref) => {

    const [enableClearBtn, setEnableClearBtn] = React.useState();
    const [hasValue, setHasValue] = React.useState(value);
    const [isFocused, setIsFocused] = React.useState(false);

    //const localRef = useRef();
    const inputRef = ref ? ref : useRef();
    const [uId] = useState(_uniqueId('text-field-'));
    const fieldId = id ? id : uId;
    const labelText = label ? `${label}${!required ? ' (Optional)' : ''}` : '';
    const showClearBtn = (isFocused && hasValue) || ((value || hasValue) && (enableClearBtn));
    const showPostIcon = postIcon && postIcon.icon && postIcon.show && postIcon.btnProps.onClick && showClearBtn;
    const _colorStyle = error ? "error" : colorStyle;
    const timeoutRef = useRef();

    useEffect(() => {
        return () => {
            clearTimeout(timeoutRef.current);
        };
    }, []);

    useEffect(() => {
        setHasValueStyle();
    }, [value]);

    const handleOnBlur = (event) => {
        let userAgent = (typeof navigator !== 'undefined' && navigator.userAgent) ? navigator.userAgent : "";
        let isSafari = (userAgent && !(/CriOS/i.test(userAgent)) && !(/FxiOS/i.test(userAgent)) && /^((?!chrome|android).)*safari/i.test(userAgent)) ? true : false;

        // Disable for Safari since autofill gets focus stuck
        if (!isSafari && event && event.relatedTarget) {
            if (event.relatedTarget?.className !== 'bsw-text-field__clear-btn' &&
                event.relatedTarget?.className !== 'bsw-text-field__post-icon-btn') {
                clearTimeout(timeoutRef.current);
                const clearBtnTimeout = setTimeout(() => { setEnableClearBtn(false) }, 100);
                timeoutRef.current = clearBtnTimeout;
            }
        }
        setIsFocused(false);

        onBlur(event);
    }

    const handleOnChange = (event) => {
        setHasValueStyle();

        if (onChange) {
            onChange(event);
        }
    }

    const handleOnFocus = (event) => {
        setIsFocused(true);
        setEnableClearBtn(true);

        onFocus(event);
    }

    const setHasValueStyle = () => {
        if (inputRef.current.value && inputRef.current.value?.length > 0) {
            setHasValue(true);
            if (isFocused) {
                setEnableClearBtn(true);
            } else {
                setEnableClearBtn(false);
            }
        } else {
            setHasValue(false);
            setEnableClearBtn(false);
        }
    }

    const clearInput = (event) => {
        if (inputRef.current) {
            inputRef.current.value = "";
            inputRef.current.focus();
        }

        handleOnChange({
            event, target: {
                name: inputRef.current.name ? inputRef.current.name : "",
                value: ""
            }
        });
    }

    const stateStyle = {
        error: {
            iconName: 'exclamation',
            iconColor: '$color-system-b1',
            msgColor: '$color-system-b1',
        },
        neutral: {
            iconName: '',
            iconColor: '',
            msgColor: '$color-secondary-a2',
        },
        success: {
            iconName: 'check',
            iconColor: '$color-system-a1',
            msgColor: '$color-system-a1',
        },
    };

    const textFieldClasses = [
        `bsw-text-field`,
        `bsw-text-field-color--${_colorStyle}`,
        `${className ? className : ''}`,
        `${icon ? 'bsw-text-field-with-icon' : ''}`,
        `${showPostIcon ? 'bsw-text-field-with-post-icon' : ''}`,
        `${disabled ? 'bsw-text-field--disabled' : ''}`,
        `${isFocused ? 'bsw-text-field--focused' : ''}`,
        `${hasValue ? 'bsw-text-field--filled' : ''}`
    ].join(' ');

    return (
        <>
            <div className={textFieldClasses}>
                {icon &&
                    <div className="bsw-text-field__icon">
                        <Icon name={icon} size="medium" />
                    </div>
                }
                <input
                    {...inputProps}
                    autoFocus={autoFocus}
                    id={fieldId}
                    name={name}
                    className="bsw-input"
                    type={inputProps.type ? inputProps.type : "text"}
                    placeholder={labelText}
                    disabled={disabled}
                    value={value}
                    ref={inputRef}
                    required={required}
                    maxLength={maxLength}
                    onBlur={handleOnBlur}
                    onChange={handleOnChange}
                    onFocus={handleOnFocus}
                />
                {labelText && <label className="bsw-text-field__label" htmlFor={fieldId}>
                    <Typography
                        type={(isFocused || value || hasValue) ? "meta-01" : "body-02"}
                        color="$color-secondary-a2"
                    >
                        {labelText}
                    </Typography>
                </label>}
                <div className="bsw-text-field__end">
                    {showPostIcon &&
                        <button
                            {...postIcon.btnProps}
                            type="button"
                            className="bsw-text-field__post-icon-btn"
                        >
                            <Icon name={postIcon.icon} size="medium" />
                        </button>
                    }
                    {showClearBtn &&
                        <button
                            type="button"
                            onClick={clearInput}
                            className="bsw-text-field__clear-btn"
                            aria-label={`Clear ${label}`}
                            onFocus={() => setEnableClearBtn(true)}
                            onBlur={() => setEnableClearBtn(false)}
                        >
                            <Icon name="x" color="$color-primary-a2" size="medium" />
                        </button>
                    }
                </div>
                <div className="bsw-text-field__hint">
                    {hintMsg &&
                        <>
                            {(_colorStyle !== 'neutral') &&
                                <div className="bsw-text-field__validation-icon">
                                    <Icon
                                        name={stateStyle[_colorStyle]['iconName']}
                                        color={stateStyle[_colorStyle]['iconColor']}
                                        size="small"
                                    />
                                </div>
                            }
                            <Typography type="meta-01" color={stateStyle[_colorStyle]['msgColor']}>
                                {hintMsg}
                            </Typography>
                        </>
                    }
                </div>
            </div>
        </>
    )
})

TextField.propTypes = {
    autoFocus: PropTypes.bool,
    className: PropTypes.string,
    disabled: PropTypes.bool,
    error: PropTypes.bool,
    colorStyle: PropTypes.oneOf(['error', 'neutral', 'success']),
    hintMsg: PropTypes.oneOfType([    
        PropTypes.string,
        PropTypes.object,
    ]),
    icon: PropTypes.string,
    id: PropTypes.string,
    label: PropTypes.string,
    maxLength: PropTypes.number,
    name: PropTypes.string,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    required: PropTypes.bool,
    value: PropTypes.string,
}